<script setup lang="ts">
import type { PhaseSteps } from "@/types"
import WarningStateImage from "@/assets/pngs/WarningState.png?w=288&h=358&format=webp&imagetools"
import FileUpload from "@/components/Controls/FileUpload.vue"
import PictogramDialog from "@/components/Dialog/PictogramDialog.vue"
import DialogHeader from "@/components/FileImport/Dialog/DialogHeader.vue"
import LocateTable from "@/components/FileImport/Locate/LocateTable.vue"
import Loading from "@/components/Loading.vue"
import messageHandler, { UIMessageKind } from "@/logic/message-handler"

import { useFileImportStore } from "@/stores"
import useAnalytics from "@/utils/analytics"

import { captureError } from "@/utils/useEngineModel"
import { useSnackbar } from "@/utils/useSnackbar"
import { DialogSize, KDialog } from "@kaleidoscope-solutions/ks-design-system"
import confetti from "canvas-confetti"
import { storeToRefs } from "pinia"
import { computed, nextTick, ref } from "vue"
import { openDialog } from "vue3-promise-dialog"

const fileImportStore = useFileImportStore()
const { loading, stepper } = storeToRefs(fileImportStore)
const { trackEvent } = useAnalytics()
const disableImport = ref(false)

// Get the current time category and cache in pinia.
await fileImportStore.retrieveTimeCategory()

const stepComponent = computed(() => {
  return stepper.value.current.value.component ?? undefined
})

const { addSnackbar } = useSnackbar()

function handleNext(step: PhaseSteps) {
  fileImportStore.goToStep(step)
}

function handleStep(step: PhaseSteps) {
  fileImportStore.goToStep(step)
}

async function handleImport() {
  try {
    await fileImportStore.importDataIntoModel()

    await nextTick()

    trackEvent(
      "file_imported",
      {
        import_duration: fileImportStore.getImportDuration,
        preview_duration: fileImportStore.getPreviewDuration
      }
    )

    closeImport()

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.2 },
      colors: ["#fd9a03", "#16a1a1", "#a414f7"],
    })
    messageHandler.emit(UIMessageKind.DataImported, undefined)
  } catch (error) {
    captureError(error)

    await openDialog<any>(PictogramDialog, {
      open: true,
      size: DialogSize.medium,
      cancelButton: undefined,
      okButton: "Close",
      imagePath: WarningStateImage,
      title: "Upload failed",
      hideCancel: true,
      body: [
        "We are sorry but it looks like something went wrong and we are not able to recover your progress.",
        "If you can remember the steps you took, note them down and let us know so we can work on it!",
      ],
    })
  }
}

function handleDisableImport(isThousandLimitErr: boolean) {
  disableImport.value = isThousandLimitErr
}

function closeImport() {
  addSnackbar({
    type: "success",
    message: `Data imported.`,
  })

  fileImportStore.closeDialog()
}

const tooltipText = computed(() => {
  if (!fileImportStore.hasTarget && !fileImportStore.isFileUploaded) {
    return "Please select a target and upload a file"
  }

  if (!fileImportStore.hasTarget) {
    return "Please select a target"
  }

  if (!fileImportStore.isFileUploaded) {
    return "Please upload a file"
  }

  return undefined
})

function checkDialog() {
  fileImportStore.openExitConfirmationDialog()
}
</script>

<template>
  <KDialog
    :open="true"
    :size="DialogSize.fullsize"
    :esc="false"
    @close="checkDialog"
  >
    <template #default>
      <DialogHeader
        :stepper="stepper"
        :tooltip-text="tooltipText"
        :disable-import="disableImport"
        @next="handleNext"
        @step="handleStep"
        @import="handleImport"
      >
        <slot>
          <div>
            <span>{{ fileImportStore.file?.file.name }} </span>
            <FileUpload>
              <template #default="{ buttonClick }">
                <button
                  class="px-2 text-sm font-bold text-violet-700 hover:text-darkViolet"
                  @click="buttonClick()"
                >
                  Replace
                </button>
              </template>
            </FileUpload>
          </div>
        </slot>
      </DialogHeader>
      <div class="relative flex h-full w-full flex-col overflow-hidden">
        <div
          v-if="loading"
          class="fixed inset-0 z-20 grid place-content-center"
        >
          <Loading class="h-40 w-40" />
        </div>
        <component
          :is="stepComponent"
          v-else-if="stepComponent"
          @disable-import="handleDisableImport"
        />

        <KDialog
          :open="fileImportStore.openFileFullDialog"
          :size="DialogSize.fullsize"
          @close="fileImportStore.openFileFullDialog = false"
        >
          <template #default>
            <div class="h-full w-full flex-1 overflow-hidden">
              <span class="text-sm font-bold"> Previewing: {{ fileImportStore.file?.file.name }} </span>
              <div class="h-full w-full overflow-auto mt-2">
                <LocateTable
                  v-if="fileImportStore.file?.scraped"
                  :table="fileImportStore.file?.scraped"
                />
              </div>
            </div>
          </template>
        </KDialog>
      </div>
    </template>
  </KDialog>
</template>
